import { Scan, FileCheck2, ScanLine, Landmark, Users, Smile, PawPrint } from "lucide-react";

export const MODALITIES = [
  {
    slug: "diagnostic-x-ray",
    name: "Diagnostic X-Ray",
    icon: Scan,
    short: "General radiography rooms for hospitals, clinics, and private practices.",
    headline: ["General radiography,", "shielded correctly."],
    intro:
      "From a single radiography room in a private practice to a multi-room hospital department, a diagnostic X-ray shielding design sizes every barrier around your actual equipment, workload, and floor plan — nothing assumed, nothing overbuilt.",
    covers: [
      "Primary barriers for the chest bucky and table directions",
      "Secondary barriers for scatter and leakage radiation",
      "Operator console and control area protection",
      "Doors, frames, and vision panels",
      "Floors and ceilings in multi-story buildings",
    ],
    projects: [
      "New radiography room construction",
      "Equipment replacement or upgrade",
      "Room relocation or remodel",
      "Chiropractic, orthopedic, and urgent care suites",
      "Shielding reviews of existing rooms",
    ],
    note: "Tube orientation and workload drive the design. The exact make and model — and where the tube points — determines which walls are primary barriers.",
    seoTitle: "Diagnostic X-Ray Shielding Design | Heartland Physics",
    seoDesc:
      "Physicist-prepared shielding designs for general radiography rooms: barrier calculations, material specifications, and state-specific documentation. Fixed $950 single-room pricing.",
  },
  {
    slug: "ct",
    name: "CT",
    icon: FileCheck2,
    short: "CT suites with demanding scatter and workload profiles.",
    headline: ["CT suites,", "calculated precisely."],
    intro:
      "CT scanners scatter radiation in nearly every direction at high weekly workloads, so every surrounding barrier matters. Your report is built from manufacturer scatter data, your clinical protocols, and your expected patient volume.",
    covers: [
      "Secondary barrier calculations for scatter and leakage",
      "Control room walls and operator positions",
      "Lead-equivalent viewing windows",
      "CT room doors and frames",
      "Floor and ceiling barriers between occupied levels",
    ],
    projects: [
      "New CT suite construction",
      "CT replacement with higher throughput",
      "Mobile CT siting",
      "Renovations around an existing scanner",
      "Multi-story installations with occupied levels above or below",
    ],
    note: "CT workload is measured in patients per week and protocol mix. Manufacturer scatter plots and isocenter placement are essential inputs — send planning drawings when available.",
    seoTitle: "CT Shielding Design | Heartland Physics",
    seoDesc:
      "Shielding calculations for CT suites: scatter-based barrier design, control rooms, viewing windows, and plan-review documentation. Fixed $950 single-room pricing.",
  },
  {
    slug: "cbct",
    name: "CBCT",
    icon: ScanLine,
    short: "Cone-beam CT in dental, ENT, orthopedic, and imaging practices.",
    headline: ["Cone-beam CT,", "right-sized."],
    intro:
      "Cone-beam CT has a small footprint, but it is still a CT-class scatter source — often placed in rooms that were never designed for it. A CBCT shielding design sizes the barriers for the room you actually have.",
    covers: [
      "Scatter and leakage barrier calculations",
      "Operator position and control area protection",
      "Walls shared with adjacent operatories and offices",
      "Doors and corridor-facing barriers",
      "Documentation formatted for plan review",
    ],
    projects: [
      "Adding CBCT to a dental or orthodontic practice",
      "ENT and orthopedic CBCT suites",
      "CBCT equipment upgrades",
      "New-build imaging suites with CBCT",
      "Unit relocations within an existing practice",
    ],
    note: "CBCT often lands in conventional office construction. The adjacent occupancy — not the machine's size — usually decides what the walls need.",
    seoTitle: "CBCT Shielding Design | Heartland Physics",
    seoDesc:
      "Cone-beam CT shielding designs for dental, ENT, and orthopedic practices: right-sized barriers and state-ready documentation. Fixed $950 single-room pricing.",
  },
  {
    slug: "fluoroscopy-c-arms",
    name: "Fluoroscopy & C-Arms",
    icon: Landmark,
    short: "Fixed fluoroscopy rooms and mobile C-arm environments.",
    headline: ["Fluoroscopy and", "C-arms, covered."],
    intro:
      "Fluoroscopy combines long beam-on times with staff working close to the patient. Shielding accounts for the table, the image-receptor direction, and the people who work in the room every day.",
    covers: [
      "Primary barriers in the image-receptor direction",
      "Scatter and leakage calculations for staff areas",
      "Control and observation areas",
      "Doors and procedure-room access points",
      "Adjacent occupied rooms and corridors",
    ],
    projects: [
      "R&F and fixed fluoroscopy rooms",
      "Pain management and GI procedure suites",
      "C-arm use in operating rooms",
      "Hybrid procedure spaces",
      "Equipment upgrades with higher usage",
    ],
    note: "Fluoroscopy workload splits between fluoro time and spot exposures. How the room is actually used — not just the machine model — sets the shielding.",
    seoTitle: "Fluoroscopy & C-Arm Shielding Design | Heartland Physics",
    seoDesc:
      "Radiation shielding design for fluoroscopy rooms and C-arm environments: barrier calculations, staff area protection, and state-specific reports. Fixed $950 single-room pricing.",
  },
  {
    slug: "mammography",
    name: "Mammography",
    icon: Users,
    short: "Mammography rooms designed to protect staff and adjacent occupants.",
    headline: ["Mammography rooms,", "done right."],
    intro:
      "Mammography uses lower energies than general radiography, but busy screening schedules and closely occupied adjacent spaces still call for a calculated, documented design.",
    covers: [
      "Barrier calculations for walls and partitions",
      "Doors and changing-area interfaces",
      "Operator and viewing areas",
      "Floor and ceiling review in multi-tenant buildings",
      "Documentation formatted for inspection",
    ],
    projects: [
      "New mammography suites",
      "2D to 3D tomosynthesis upgrades",
      "Screening center buildouts",
      "Room relocations within a clinic",
      "Mobile mammography siting reviews",
    ],
    note: "Lower kVp does not mean no shielding review. Adjacent occupancy and weekly workload still determine the barrier requirements.",
    seoTitle: "Mammography Shielding Design | Heartland Physics",
    seoDesc:
      "Shielding designs for mammography and tomosynthesis rooms: right-sized barriers, inspection-ready documentation, and fast turnaround. Fixed $950 single-room pricing.",
  },
  {
    slug: "dental",
    name: "Dental",
    icon: Smile,
    short: "Intraoral, panoramic, and CBCT imaging in dental practices.",
    headline: ["Dental imaging,", "without overbuilding."],
    intro:
      "Dental operatories are small, numerous, and side by side — so adjacency drives the design. Right-sized calculations keep your practice compliant without lining every wall in lead.",
    covers: [
      "Intraoral and panoramic X-ray rooms",
      "Dental CBCT operatories",
      "Walls between adjacent operatories",
      "Operator positions and handheld-unit use",
      "Doors and corridor-facing barriers",
    ],
    projects: [
      "New dental practice buildouts",
      "Adding panoramic or CBCT imaging",
      "Multi-operatory layouts",
      "Equipment replacement and room changes",
      "Shielding reviews for existing practices",
    ],
    note: "In dental work, the neighboring operatory is often feet away. Occupancy on the other side of each wall is the deciding factor.",
    seoTitle: "Dental X-Ray Shielding Design | Heartland Physics",
    seoDesc:
      "Shielding designs for dental X-ray, panoramic, and CBCT rooms: right-sized barriers without overbuilding. Fixed $950 single-room pricing.",
  },
  {
    slug: "veterinary",
    name: "Veterinary",
    icon: PawPrint,
    short: "Veterinary radiography, dental, and CT for animal hospitals.",
    headline: ["Veterinary imaging,", "calculated safely."],
    intro:
      "Veterinary imaging adds a wrinkle human imaging doesn't have: staff may be in the room during exposures. A veterinary shielding design accounts for the room, the equipment, and the people holding the patient.",
    covers: [
      "Veterinary radiography rooms",
      "Veterinary CT and dental imaging",
      "Operator positions where manual restraint is used",
      "Walls, doors, and adjacent treatment areas",
      "Documentation formatted for plan review",
    ],
    projects: [
      "New veterinary clinic imaging rooms",
      "Adding CT to an animal hospital",
      "Veterinary dental suites",
      "Mobile veterinary imaging",
      "Clinic remodels and equipment upgrades",
    ],
    note: "Veterinary workloads and restraint practices differ from human medicine — that changes who needs protecting, and where they stand.",
    seoTitle: "Veterinary X-Ray Shielding Design | Heartland Physics",
    seoDesc:
      "Shielding designs for veterinary radiography, dental, and CT rooms — including operator protection where manual restraint is used. Fixed $950 single-room pricing.",
  },
];
